import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import App from 'antd/lib/app';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import Layout from 'antd/lib/layout';
import { useContext, useMemo, useReducer } from 'react';
import { AbilityContext } from 'src/components/ability/can';
import { ActionList, IAction } from 'src/components/table/actionList/actionList';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { compareWithLocale } from '../../../domain/compareWithLocale';
import { ModalState, ModalStateAction, PjmRegistrationsModal, reducer } from './pjmRegistrationsReducer';
import { PageHeader } from 'src/components/PageHeader';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { PjmRegistrationModal } from './components/PjmRegistrationModal/PjmRegistrationModal';
import { AuditTrailModal } from 'src/components/modals/AuditTrailModal/AuditTrailModal';
import { AuditEntityType } from 'src/domain/audit-trail/interface';
import { PjmBreadcrumbNav } from '../PjmBreadcrumbNav';
import { usePjmRegistrationsListQuery } from 'src/domain/pjm/queries';
import { DrhubSyncStatus } from '../DrhubSyncStatus';
import { StatusTag } from 'src/components/StatusTag';
import { usePageLocation } from '../../usePageState';
import { useLocation } from 'react-router-dom';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { toUsefulParams } from '../../toUsefulParams';
import { getTablePaginationParams } from 'src/components/table/getTablePaginationParams';
import { SearchInput } from 'src/components/searchInput/searchInput';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { DEFAULT_DATE_FORMAT, getTimezoneFormattedTime } from 'src/domain/date/date';
import Button from 'antd/lib/button';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import { PjmRegistrationId } from './components/PjmRegistrationId';
import { PjmRegistrationUploadMeterDataModal } from './components/PjmRegistrationUploadMeterDataModal/PjmRegistrationUploadMeterDataModal';
import { PjmRegistrationsImportModal } from './components/PjmRegistrationsImportModal';
import { PJM_TZ } from 'src/domain/pjm';
import { getPjmSeasonInfo } from '../../../domain/pjm/season/getPjmSeasonInfo';
import { usePjmSeasonQuery } from '../../../domain/pjm/season/queries';
import Popover from 'antd/lib/popover';
import { WarningTwoTone } from '@ant-design/icons';
import { WARNING_COLOR } from '../../../theme';
import { PjmSeasonModal } from './components/PjmSeasonModal/PjmSeasonModal';
import { Tooltip } from 'antd/lib';
import { usePjmRegistrationsSidebar } from './components/PjmRegistrationSidebar/usePjmRegistrationsSidebar';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { PjmRegistrationSidebar } from './components/PjmRegistrationSidebar/PjmRegistrationSidebar';
import Tag from 'antd/lib/tag';
import { useTableSelection } from 'src/components/table/useTableSelection';
import { Key } from 'antd/lib/table/interface';
import { PjmRegistrationsBulkSubmitModal } from './components/PjmRegistrationsBulkSubmitModal/PjmRegistrationsBulkSubmitModal';
import { SidebarGrid } from '../../../components/Sidebar/SidebarGrid';

import './PjmRegistrations.css';

export const PjmRegistrations = () => {
    const { notification } = App.useApp();

    useDocumentTitle('PJM Registrations');

    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = usePjmRegistrationsSidebar();
    const ability = useContext(AbilityContext);
    const location = useLocation();

    const initialState: ModalState = { modal: undefined, target: undefined };
    const [modalState, dispatch] = useReducer(reducer, initialState);

    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);

    const { selectedKeys, selectedItems, handleSelectedItems } = useTableSelection('uuid');

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        ...(pageState?.filter && { filter: pageState?.filter }),
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.search && { search: pageState?.search }),
        include: 'location',
    };

    const { data: registrations, isError, error, isFetching, refetch: refetchPjmRegistrations } = usePjmRegistrationsListQuery(listQuery);

    if (isError) {
        notification.error({ key: 'fetch-pjm-registrations-error', message: error.message || 'Cannot fetch PJM Registrations!' });
    }

    const currentSeasonInfo = useMemo(() => getPjmSeasonInfo(), []);

    const {
        data: seasonData,
        isLoading: isSeasonLoading,
        isError: isSeasonError,
        error: seasonError,
        refetch: refetchSeason,
    } = usePjmSeasonQuery(currentSeasonInfo.id, { enabled: currentSeasonInfo.period === 'winter' });

    const isSeasonalDataMissing = currentSeasonInfo.period === 'winter' && !seasonData && !isSeasonError && !isSeasonLoading;

    if (isSeasonError) {
        notification.error({
            key: 'pjm-season-fetch-error',
            message: seasonError?.message || 'Cannot fetch PJM season!',
        });
    }

    async function onTableChange(
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<PjmRegistrationDto> | SorterResult<PjmRegistrationDto>[]
    ) {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, sorter, filter: newFilter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onSearch = async (value: string) => {
        setPageQuery({ ...pageState, search: value });
    };

    const onSelectRow = (selectedRows: Key[]) => {
        handleSelectedItems(selectedRows as number[], registrations?.data as any[]);
    };

    function onModalClose(actionTaken: boolean) {
        dispatch({ type: 'CLOSE' });
        if (actionTaken) {
            handleSelectedItems([], []);
            refetchPjmRegistrations();
            currentSeasonInfo.period === 'winter' && refetchSeason();
        }
    }

    function makeYesOrNoTag(value: boolean | null | undefined) {
        switch (value) {
            case true:
                return <Tag color="success">Yes</Tag>;
            case false:
                return <Tag color="error">No</Tag>;
            default:
                return '';
        }
    }

    const actions: IAction[] = [
        {
            title: 'Edit',
            onClick: (record: PjmRegistrationDto) => () => dispatch({ type: 'EDIT', payload: record }),
        },
        {
            title: 'Audit Trail',
            icon: <HistoryOutlined />,
            onClick: (record: PjmRegistrationDto) => () => dispatch({ type: 'AUDIT', payload: record }),
        },
    ];

    const columns: CustomizableColumnType<PjmRegistrationDto>[] = [
        {
            key: 'registration_name',
            title: 'Registration',
            dataIndex: ['summaryInfo', 'name'],
            minWidth: 200,
            fixed: 'left',
            sorter: (a, b) => compareWithLocale(a.summaryInfo.name, b.summaryInfo.name),
            render: (text, record) => <>{text}</>,
        },
        {
            key: 'registration_id',
            title: 'Registration ID',
            dataIndex: 'id',
            minWidth: 100,
            sorter: (a, b) => compareWithLocale(a.id, b.id),
            render: (text, record) => <PjmRegistrationId value={text} />,
        },
        {
            key: 'program',
            title: 'Program',
            dataIndex: ['summaryInfo', 'program'],
            minWidth: 100,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.program, b.summaryInfo.program),
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: ['summaryInfo', 'status'],
            minWidth: 120,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.status, b.summaryInfo.status),
            render: (text, record) => <StatusTag status={text} />,
        },
        {
            key: 'effective_date',
            title: 'Effective Date',
            dataIndex: ['summaryInfo', 'startDate'],
            minWidth: 40,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.startDate?.valueOf(), b.summaryInfo.startDate?.valueOf()),
            render: (text, record) => <>{getTimezoneFormattedTime(text, PJM_TZ, DEFAULT_DATE_FORMAT)}</>,
        },
        {
            key: 'termination_date',
            title: 'Termination Date',
            dataIndex: ['summaryInfo', 'endDate'],
            minWidth: 40,
            sorter: (a, b) => compareWithLocale(a.summaryInfo.endDate?.valueOf(), b.summaryInfo.endDate?.valueOf()),
            render: (text, record) => <>{getTimezoneFormattedTime(text, PJM_TZ, DEFAULT_DATE_FORMAT)}</>,
        },
        {
            key: 'zone',
            title: 'Zone',
            dataIndex: ['summaryInfo', 'zone'],
            minWidth: 60,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.zone, b.summaryInfo?.zone),
        },
        {
            key: 'pricing_point',
            title: 'Pricing Point',
            dataIndex: ['summaryInfo', 'pricingPoint'],
            minWidth: 220,
            hidden: true, // Hide by default
        },
        {
            key: 'csp_name',
            title: 'CSP',
            dataIndex: ['summaryInfo', 'CSP', 'name'],
            minWidth: 60,
            hidden: true, // Hide by default
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.CSP?.name, b.summaryInfo?.CSP?.name),
        },
        {
            key: 'csp_account_number',
            title: 'CSP Account Number',
            dataIndex: ['summaryInfo', 'CSP', 'accountNo'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'edc_name',
            title: 'EDC',
            dataIndex: ['summaryInfo', 'EDC', 'name'],
            minWidth: 60,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.EDC?.name, b.summaryInfo?.EDC?.name),
        },
        {
            key: 'edc_account_number',
            title: 'EDC Account Number',
            dataIndex: ['summaryInfo', 'EDC', 'accountNo'],
            minWidth: 100,
            hidden: true, // Hide by default
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.EDC?.accountNo, b.summaryInfo?.EDC?.accountNo),
        },
        {
            key: 'lse_name',
            title: 'LSE',
            dataIndex: ['summaryInfo', 'LSE', 'name'],
            minWidth: 60,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.LSE?.name, b.summaryInfo?.LSE?.name),
        },
        {
            key: 'lse_account_number',
            title: 'LSE Account Number',
            dataIndex: ['summaryInfo', 'LSE', 'accountNo'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'resource_type',
            title: 'Resource Type',
            dataIndex: ['capacityInfo', 'resourceType'],
            minWidth: 80,
        },
        {
            key: 'product',
            title: 'Product',
            dataIndex: ['capacityInfo', 'product'],
            minWidth: 80,
        },
        {
            key: 'lead_time',
            title: 'Lead Time',
            dataIndex: ['capacityInfo', 'leadTime'],
            minWidth: 80,
        },
        {
            key: 'completed',
            title: 'Completed',
            dataIndex: ['summaryInfo', 'completed'],
            minWidth: 120,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.completed, b.summaryInfo?.completed),
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.completed),
        },
        {
            key: 'dispatch_group',
            title: 'Dispatch Group',
            dataIndex: ['summaryInfo', 'dispatchGroup'],
            minWidth: 120,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.dispatchGroup, b.summaryInfo?.dispatchGroup),
        },
        {
            key: 'under_duplicate_review',
            title: 'Under Duplicate Review',
            dataIndex: ['summaryInfo', 'underDuplicateReview'],
            minWidth: 120,
            sorter: (a, b) => compareWithLocale(a.summaryInfo?.underDuplicateReview, b.summaryInfo?.underDuplicateReview),
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.underDuplicateReview),
        },
        // {
        //     title: 'Under CBL Review',
        //     dataIndex: ['summaryInfo', 'underCblReview'], // todo: find correct filed for this!
        //     minWidth: 120,
        //     // @ts-expect-error
        //     sorter: (a, b) => sortingComparator(String(a.summaryInfo?.underCblReview), String(b.summaryInfo?.underCblReview)),
        //     filterMultiple: false,
        //     filters: REGISTRATION_YES_NO_FILTER,
        //     ...getColumnFilteredValue('capacityInfo.underCblReview', pageState?.filter || {}),
        // },
        {
            key: 'sync_drhub_status',
            title: 'DRHUB Sync',
            dataIndex: 'syncDrhubStatus',
            minWidth: 120,
            render: (text, record) => <DrhubSyncStatus value={record} />,
        },
        {
            key: 'residential_registration',
            title: 'Residential Registration?',
            dataIndex: ['summaryInfo', 'residential'],
            minWidth: 100,
            hidden: true, // ide by default
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.residential),
        },
        {
            key: 'residential_program_type',
            title: 'Residential Program Type?',
            dataIndex: ['summaryInfo', 'residentialProgramType'],
            minWidth: 150,
            hidden: true, // ide by default
        },
        {
            key: 'load_data_sample',
            title: 'Load Data Sample?',
            dataIndex: ['summaryInfo', 'loadDataSample'],
            minWidth: 100,
            hidden: true, // ide by default
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.loadDataSample),
        },
        {
            key: 'sample_number_participants',
            title: '# Sample Participants',
            dataIndex: ['summaryInfo', 'sampleNumberParticipants'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'allow_edc_to_modify',
            title: 'Allow EDC to Modify',
            dataIndex: ['otherInfo', 'allowEDCtoModify'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.otherInfo?.allowEDCtoModify),
        },
        {
            key: 'allow_lse_to_modify',
            title: 'Allow LSE to Modify',
            dataIndex: ['otherInfo', 'allowLSEtoModify'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.otherInfo?.allowLSEtoModify),
        },
        {
            key: 'test_required',
            title: 'Test Required',
            dataIndex: ['summaryInfo', 'testRequired'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.summaryInfo?.testRequired),
        },
        {
            key: 'hold_scheduling',
            title: 'Hold Scheduling',
            dataIndex: ['otherInfo', 'holdScheduling'],
            minWidth: 60,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.otherInfo?.holdScheduling),
        },
        {
            key: 'hold_settlements',
            title: 'Hold Settlements',
            dataIndex: ['otherInfo', 'holdSettlements'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.otherInfo?.holdSettlements),
        },
        {
            key: 'peak_load_contribution',
            title: 'Peak Load Contribution (kW)',
            dataIndex: ['locationSummaryInfo', 'peakLoadContribution'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'winter_peak_load',
            title: 'Winter Peak Load (kW)',
            dataIndex: ['locationSummaryInfo', 'winterPeakLoad'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'measurement_method',
            title: 'Measurement Method',
            dataIndex: ['capacityInfo', 'measurementMethod'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'winter_managed_load',
            title: 'Winter Managed Load (kW)',
            dataIndex: ['capacityInfo', 'winterManagedLoad'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'winter_weather_adjustment_factor',
            title: 'Winter Weather Adj. Factor',
            dataIndex: ['capacityInfo', 'winterWeatherAdjustmentFactor'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'summer_managed_load',
            title: 'Summer Managed Load (kW)',
            dataIndex: ['capacityInfo', 'summerManagedLoad'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'rpm_resource_id',
            title: 'RPM DR Resource ID',
            dataIndex: ['capacityInfo', 'RPMResourceID'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'capacity_loss_factor',
            title: 'Capacity Loss Factor',
            dataIndex: ['locationSummaryInfo', 'capacityLossFactor'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'winter_nominated_icap',
            title: 'Winter Nominated ICAP (kW)',
            dataIndex: ['capacityInfo', 'winterNominatedICAP'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'summer_nominated_icap',
            title: 'Summer Nominated ICAP (kW)',
            dataIndex: ['capacityInfo', 'summerNominatedICAP'],
            minWidth: 100,
            hidden: true, // Hide by default
        },
        {
            key: 'lead_exception_certify',
            title: 'Lead Exception Certify',
            dataIndex: ['capacityInfo', 'leadExceptionCertify'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.capacityInfo?.leadExceptionCertify),
        },
        {
            key: 'cbl_method_name',
            title: 'CBL Method',
            dataIndex: ['cblMethodInfo', 'methodName'],
            minWidth: 200,
            hidden: true, // Hide by default
        },
        {
            key: 'summer_only',
            title: 'Summer Only',
            dataIndex: ['capacityInfo', 'summerOnly'],
            minWidth: 100,
            hidden: true, // Hide by default
            render: (text, record) => makeYesOrNoTag(record.capacityInfo?.summerOnly),
        },
        {
            key: 'strike_price',
            title: 'Strike Price ($/Mwh)',
            dataIndex: ['energyInfo', 'strikePrice'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        {
            key: 'shutdown_costs',
            title: 'Shut Down Costs ($)',
            dataIndex: ['energyInfo', 'shutdownCosts'],
            minWidth: 150,
            hidden: true, // Hide by default
        },
        ...(ability.can('update', 'PjmRegistration')
            ? ([
                  {
                      key: 'action',
                      fixed: 'right',
                      minWidth: 60,
                      render: (text, record) => <ActionList actions={actions} item={record} />,
                  },
              ] as CustomizableColumnType<PjmRegistrationDto>[])
            : []),
    ];

    const pageHeaderActions: React.ReactNode[] = [];
    if (selectedKeys.length > 0) {
        pageHeaderActions.push(
            <Button size="large" key="clear-selection-action" onClick={() => handleSelectedItems([], [])}>
                Clear Selected ({selectedKeys.length})
            </Button>
        );
    }

    pageHeaderActions.push(<SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="search-pjm-registrations" />);

    if (ability.can('update', 'PjmRegistration')) {
        pageHeaderActions.push(<SubmitRegistrationsButton dispatch={dispatch} registrations={selectedItems} />);
    }

    const peakLoadDaysButton = (
        <Button
            size="large"
            key="pjm-season-create"
            onClick={() => dispatch({ type: 'EDIT_SEASON' })}
            disabled={currentSeasonInfo.period === 'summer'}
        >
            {isSeasonalDataMissing && (
                <Popover content={<div>Peak Load days for current season is missing!</div>}>
                    <WarningTwoTone twoToneColor={WARNING_COLOR} style={{ fontSize: 'large' }} />
                </Popover>
            )}
            Peak Load Days
        </Button>
    );

    if (ability.can('create', 'PjmSeason')) {
        pageHeaderActions.push(
            currentSeasonInfo.period === 'summer' ? (
                <Tooltip
                    title={
                        <>
                            <div>Peak Load calculation is not available during the Summer season.</div>
                            <div>
                                You can enter Peak Load Days during the Winter season (<strong>01 Nov – 30 Apr</strong>).
                            </div>
                        </>
                    }
                >
                    {peakLoadDaysButton}
                </Tooltip>
            ) : (
                peakLoadDaysButton
            )
        );
    }

    if (ability.can('create', 'PjmRegistration')) {
        pageHeaderActions.push(
            <Button size="large" key="pjm-registrations-import" onClick={() => dispatch({ type: 'IMPORT' })} icon={<UploadOutlined />}>
                Import
            </Button>
        );

        pageHeaderActions.push(
            <Button
                size="large"
                key="pjm-registrations-upload-meter-data"
                onClick={() => dispatch({ type: 'UPLOAD_METER_DATA' })}
                icon={<UploadOutlined />}
            >
                Upload Meter Data
            </Button>
        );
    }

    return (
        <>
            <CustomizableTableContextProvider configKey="pjmRegistrations" columns={columns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    {isFiltersSidebarEnabled && (
                        <PjmRegistrationSidebar
                            collapsed={sidebarCollapsed}
                            onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                            totalRecords={getTablePaginationParams(registrations?.meta).total}
                            onFilterChange={() => {}}
                            gridComponent={<SidebarGrid />}
                        />
                    )}

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader pageTitle={<PjmBreadcrumbNav />} actions={pageHeaderActions} />
                        <WithEmptyDataTable condition={isFetching}>
                            <CustomizableTable<PjmRegistrationDto>
                                showHiddenColumnsCount
                                size="small"
                                rowKey="uuid"
                                className="pjm-registrations-table"
                                sticky={{ offsetHeader: 62 }}
                                dataSource={registrations?.data || []}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={registrations?.meta!}
                                rowSelection={
                                    ability.can('update', 'PjmRegistration')
                                        ? {
                                              selectedRowKeys: selectedKeys,
                                              preserveSelectedRowKeys: true,
                                              onChange: onSelectRow,
                                          }
                                        : undefined
                                }
                            />
                        </WithEmptyDataTable>
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>

            {modalState.modal === PjmRegistrationsModal.EDIT && (
                <PjmRegistrationModal key="pjm-registration-modal" onClose={onModalClose} value={modalState.target} seasonData={seasonData} />
            )}
            {modalState.modal === PjmRegistrationsModal.IMPORT && (
                <PjmRegistrationsImportModal key="pjm-registration-import-modal" onClose={onModalClose} />
            )}
            {modalState.modal === PjmRegistrationsModal.UPLOAD_METER_DATA && (
                <PjmRegistrationUploadMeterDataModal key="pjm-registration-upload-meter-data-modal" onClose={onModalClose} />
            )}
            {modalState.modal === PjmRegistrationsModal.AUDIT && (
                <AuditTrailModal
                    key="pjm-audit-trail-modal"
                    onClose={() => dispatch({ type: 'CLOSE' })}
                    entityId={modalState.target?.uuid!}
                    entityType={AuditEntityType.PJM_REGISTRATION}
                    pageSize={100}
                />
            )}
            {modalState.modal === PjmRegistrationsModal.EDIT_SEASON && (
                <PjmSeasonModal seasonId={currentSeasonInfo.id} seasonData={seasonData} onClose={onModalClose} />
            )}
            {modalState.modal === PjmRegistrationsModal.BULK_SUBMIT && (
                <PjmRegistrationsBulkSubmitModal registrations={selectedItems} onClose={onModalClose} />
            )}
        </>
    );
};

function SubmitRegistrationsButton({
    dispatch,
    registrations,
}: {
    dispatch: (value: ModalStateAction) => void;
    registrations: PjmRegistrationDto[];
}) {
    const tooltip =
        registrations.length === 0
            ? 'Select at least one registration to submit.'
            : `Submit ${registrations.length} selected registrations. You will see a dialog to review and confirm submit.`;

    return (
        <Tooltip title={tooltip}>
            <Button
                size="large"
                key="pjm-registrations-bulk-submit"
                onClick={() => dispatch({ type: 'BULK_SUBMIT' })}
                disabled={registrations.length === 0}
            >
                Submit
            </Button>
        </Tooltip>
    );
}
